import React from 'react';
import { X, ArrowLeft, Sparkles, Zap, Brain, Globe, Users, Lightbulb } from 'lucide-react';

export default function VisionPage({ onClose }) {
    return (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-200 z-10">
                <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-gray-700 hover:text-amber-600 transition"
                    >
                        <ArrowLeft size={20} />
                        <span className="font-medium">Back</span>
                    </button>
                    <button
                        onClick={onClose}
                        className="text-gray-700 hover:text-amber-600 transition"
                    >
                        <X size={24} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-6 py-16">
                {/* Hero Section */}
                <div className="mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full border border-amber-300 mb-6">
                        <Sparkles size={16} className="text-amber-600" />
                        <span className="text-amber-900 text-sm font-semibold">The Manifesto</span>
                    </div>
                    <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Beyond Retrofitting: <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">Reimagining Life with AI</span>
                    </h1>
                    <p className="text-xl text-gray-600 leading-relaxed">
                        The future isn't about adding AI to what exists—it's about fundamentally rethinking how we live, work, and thrive in a world where intelligence is ambient.
                    </p>
                </div>

                {/* The Problem Section */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 md:p-12 border border-gray-200">
                        <div className="flex items-start gap-6 mb-6">
                            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                                <Zap size={32} className="text-red-600" />
                            </div>
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-4">The Retrofitting Trap</h2>
                                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                    Today's AI adoption follows a predictable pattern: take existing workflows, existing jobs, existing systems—and bolt on AI capabilities. A chatbot here, an automation there, a generative model to speed up content creation.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    This approach is fundamentally limited. It treats AI as a tool to make the old world slightly more efficient, rather than as a catalyst for creating an entirely new world.
                                </p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 mt-8">
                            <div className="bg-white rounded-xl p-6 border border-gray-200">
                                <h3 className="font-bold text-gray-900 mb-2">Limited Thinking</h3>
                                <p className="text-sm text-gray-600">Constraining AI to fit existing processes instead of reimagining the processes themselves</p>
                            </div>
                            <div className="bg-white rounded-xl p-6 border border-gray-200">
                                <h3 className="font-bold text-gray-900 mb-2">Missed Potential</h3>
                                <p className="text-sm text-gray-600">Failing to unlock AI's transformative capabilities by keeping it in an assistant role</p>
                            </div>
                            <div className="bg-white rounded-xl p-6 border border-gray-200">
                                <h3 className="font-bold text-gray-900 mb-2">Short-Term Focus</h3>
                                <p className="text-sm text-gray-600">Optimizing for immediate productivity gains while missing the bigger transformation</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Our Vision Section */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 md:p-12 border border-amber-200">
                        <div className="flex items-start gap-6 mb-6">
                            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                                <Brain size={32} className="text-amber-600" />
                            </div>
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-4">Synthetic Sapience</h2>
                                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                    We envision a world where Artificial Intelligence isn't something you "use"—it's woven into the fabric of existence. Sapient, invisible, always present, always empowering.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    Imagine waking up in a world where AI has already optimized your health, your schedule, your environment. Where creative work flows effortlessly because AI handles the mechanics while you focus on vision. Where learning is personalized to your unique cognitive patterns. Where communities self-organize around shared values, facilitated by intelligent systems that understand human nuance.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Key Principles */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Principles</h2>

                    <div className="space-y-6">
                        <div className="flex gap-6 items-start">
                            <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Globe size={24} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">1. Safety First, Always</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    Synthetic Sapience must be safe by design. We build systems with robust safeguards, transparent decision-making, and human oversight where it matters most. Intelligence without safety is dangerous; safety without intelligence is stagnation.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-6 items-start">
                            <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Users size={24} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">2. Human Empowerment, Not Replacement</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    AI should unlock human potential, not diminish it. Our solutions amplify creativity, augment decision-making, and free people to focus on what makes us uniquely human: empathy, imagination, and meaning-making.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-6 items-start">
                            <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Lightbulb size={24} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">3. Reimagine, Don't Retrofit</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    Every solution we build starts with a fundamental question: "If we were designing this from scratch in an AI-native world, what would it look like?" We don't accept the constraints of the past.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-6 items-start">
                            <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Sparkles size={24} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">4. Holistic Integration</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    True synthetic sapience requires AI to be present across every dimension of life—work, health, education, creativity, community. Siloed solutions create fragmented experiences. We build for the whole.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* What We Offer */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">What Deep 9 Offers</h2>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-amber-300 hover:shadow-lg transition">
                            <div className="text-4xl mb-4">🎯</div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Strategic Expertise</h3>
                            <p className="text-gray-700 leading-relaxed">
                                We help organizations understand what's possible when you stop retrofitting and start reimagining. Our consultants bring deep technical knowledge and visionary thinking to guide your transformation.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-amber-300 hover:shadow-lg transition">
                            <div className="text-4xl mb-4">🛠️</div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Custom Solutions</h3>
                            <p className="text-gray-700 leading-relaxed">
                                Every organization's path to sapience is unique. We design and build bespoke systems tailored to your specific context, challenges, and ambitions—not one-size-fits-all products.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-amber-300 hover:shadow-lg transition">
                            <div className="text-4xl mb-4">🧠</div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Nonary Lattice Framework</h3>
                            <p className="text-gray-700 leading-relaxed">
                                Our proprietary approach integrates nine types of intelligence—linguistic, visual, logical, audio, physical, interpersonal, intrapersonal, natural, and existential—to create truly holistic AI systems.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-amber-300 hover:shadow-lg transition">
                            <div className="text-4xl mb-4">🚀</div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Implementation Support</h3>
                            <p className="text-gray-700 leading-relaxed">
                                Vision without execution is just dreaming. We partner with you through the entire journey—from strategy to deployment to continuous evolution—ensuring your reimagined future becomes reality.
                            </p>
                        </div>
                    </div>
                </section>

                {/* The Future Section */}
                <section className="mb-16">
                    <div className="bg-gradient-to-r from-amber-600 via-orange-600 to-amber-600 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full mix-blend-multiply filter blur-3xl"></div>
                            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white rounded-full mix-blend-multiply filter blur-3xl"></div>
                        </div>

                        <div className="relative z-10">
                            <h2 className="text-3xl md:text-4xl font-bold mb-6">The Future We're Building</h2>
                            <p className="text-xl text-amber-50 leading-relaxed mb-6">
                                In ten years, the distinction between "AI-powered" and "normal" will disappear. Intelligence will be as ubiquitous as electricity. The question won't be "Do you use AI?" but "How well does your synthetic sapience understand and empower you?"
                            </p>
                            <p className="text-lg text-amber-100 leading-relaxed mb-8">
                                Deep 9 is building the infrastructure, expertise, and platforms—Sapix first among them—to make that future not just possible, but inevitable. We're not waiting for the world to change—we're actively reimagining it.
                            </p>
                            <p className="text-xl font-bold text-white">
                                The question is: Will you be part of this transformation, or will you be left retrofitting in a reimagined world?
                            </p>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Ready to Reimagine?</h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Let's explore how Deep 9 can help you move beyond retrofitting and embrace the full potential of synthetic sapience.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button className="px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-amber-600/40 transition transform hover:scale-105">
                            Start a Conversation
                        </button>
                        <button
                            onClick={onClose}
                            className="px-8 py-4 bg-gray-200 text-gray-900 rounded-xl font-bold text-lg hover:bg-gray-300 transition"
                        >
                            Back to Home
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
}
